import { useQueries, useQuery } from '@tanstack/react-query'
import { onLine, PrintRows, PrintRunning, type Rec, resourceApi, SignatureMark, useCompany } from '@tnn/app-kit'
import { bahtText, formatDate, formatMoney, formatPhone, formatTaxId, type RefMap } from '@tnn/core'
import { invoice } from '@tnn/domain'
import { useTranslation } from 'react-i18next'
import classes from './document.module.css'
import { CustomerAddress, CustomerTaxLine, LinesClose, useIssuedSignatures } from './InvoiceDocument'

/**
 * A4 billing note: the invoices handed over, their total in words, the date the customer will
 * pay, and signatures of who billed and who received it. Same page layout as the invoice.
 */
export function BillingNoteDocument({ record, refs, copy = false }: { record: Rec; refs: RefMap; copy?: boolean }) {
  const { t } = useTranslation()
  const company = useCompany().data
  const signed = useIssuedSignatures('accounting.billingNote', record)
  const customer = useQuery({
    queryKey: ['r', 'accounting.customer', 'record', String(record.customerId)],
    queryFn: () => resourceApi('accounting.customer').get(String(record.customerId)),
    enabled: !!record.customerId,
  }).data?.item
  const ids = (record.invoiceIds ?? []) as string[]
  const invoices = useQueries({
    queries: ids.map((id) => ({ queryKey: ['r', invoice.key, 'record', id], queryFn: () => resourceApi(invoice.key).get(id) })),
  })
    .map((q) => q.data?.item)
    .filter((i): i is Rec => !!i)
  const total = invoices.reduce((sum, i) => sum + Number(i.total ?? 0), 0)
  const branch = (b?: string | null) =>
    !b || b === '00000' ? t('accounting:customer.headOffice') : `${t('accounting:customer.fields.branch')} ${b}`
  const f = (k: string) => t(`accounting:billingNote.fields.${k}`)
  const p = (k: string) => t(`accounting:billingNote.print.${k}`)
  const inv = (k: string) => t(`accounting:invoice.fields.${k}`)
  const docName = t('accounting:billingNote.documentTitle')
  const billedOn = formatDate(String(record.billedOn), { style: 'long' })
  const copyLabel = t(copy ? 'accounting:invoice.print.copy' : 'accounting:invoice.print.original')

  return (
    <article className={classes.doc}>
      <PrintRunning start={[company?.name, docName]} end={[String(record.number ?? ''), billedOn, copyLabel]} />
      <header className={classes.head}>
        <div className={classes.from}>
          <div className={classes.company}>{company?.name}</div>
          <div>{company?.address}</div>
          <div>
            {company?.taxId && `${t('accounting:customer.fields.taxId')} ${formatTaxId(company.taxId)} · ${branch(company.branch)}`}
          </div>
          <div>{[formatPhone(company?.phone), company?.email].filter(Boolean).join(' · ')}</div>
        </div>
        <div className={classes.title}>
          <div className={classes.docName}>{docName}</div>
          <div className={classes.copy}>{copyLabel}</div>
        </div>
      </header>

      <section className={classes.parties}>
        <div>
          <div className={classes.caption}>{t('accounting:invoice.print.billTo')}</div>
          <div className={classes.strong}>{String(customer?.name ?? refs['accounting.customer']?.[String(record.customerId)] ?? '')}</div>
          <CustomerAddress customer={customer} />
          <CustomerTaxLine customer={customer} />
        </div>
        <dl className={classes.meta}>
          <dt>{f('number')}</dt>
          <dd className={classes.strong}>{String(record.number ?? '')}</dd>
          <dt>{f('billedOn')}</dt>
          <dd>{billedOn}</dd>
          {record.dueDate ? (
            <>
              <dt>{f('dueDate')}</dt>
              <dd className={classes.strong}>{formatDate(String(record.dueDate), { style: 'long' })}</dd>
            </>
          ) : null}
        </dl>
      </section>

      <table className={classes.lines}>
        <thead>
          <tr>
            <th style={{ width: '6%' }}>#</th>
            <th>{inv('number')}</th>
            <th style={{ width: '18%' }}>{inv('issueDate')}</th>
            <th style={{ width: '18%' }}>{inv('dueDate')}</th>
            <th data-num style={{ width: '20%' }}>
              {inv('total')}
            </th>
          </tr>
        </thead>
        <PrintRows
          rows={invoices.map((i, n) => (
            <tr key={i.id}>
              <td>{n + 1}</td>
              <td>{String(i.number ?? '')}</td>
              <td>{formatDate(String(i.issueDate))}</td>
              <td>{i.dueDate ? formatDate(String(i.dueDate)) : '–'}</td>
              <td data-num>{formatMoney(Number(i.total ?? 0))}</td>
            </tr>
          ))}
          close={
            <LinesClose columns={5}>
              <section className={classes.summary}>
                <div className={classes.words}>
                  <div className={classes.caption}>{t('accounting:invoice.totals.inWords')}</div>
                  <div className={classes.strong}>({bahtText(total)})</div>
                  {record.notes ? <p className={classes.notes}>{String(record.notes)}</p> : null}
                </div>
                <dl className={classes.totals}>
                  <dt className={classes.grand}>{f('amount')}</dt>
                  <dd className={classes.grand}>{formatMoney(total)}</dd>
                </dl>
              </section>

              <footer className={classes.signs}>
                <SignatureMark label={p('biller')} signature={onLine(signed, 'biller')} />
                <SignatureMark label={p('receiver')} signature={onLine(signed, 'customer')} />
              </footer>
              {record.dueDate ? null : <div className={classes.caption}>{p('payOn')} ____/____/________</div>}
            </LinesClose>
          }
        />
      </table>
    </article>
  )
}
